/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import ReactFlow, {
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  Position,
  useNodesInitialized,
} from "reactflow";
import "reactflow/dist/style.css";
import { useQuery } from "@tanstack/react-query";
import { getSitePowerCapacityQuery } from "../../../lib/Queries";
import CustomNode from "./CustomNode";
import { nodesOrder, loadChilds } from "../Logic/formatUtils";
import Loading from "../../Shared/Loading";
import DataComponentError from "../../Shared/DataComponentError";
import { Box, Typography } from "@mui/material";
import TimeAgo from "react-timeago";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import OtherAlarms from "./OtherAlarms";
import AssetDetailModal from "../../Common/AssetDetailModal";
import AlarmsModal from "../Common/AlarmsModal";
import { sortAlarmsBySeverity } from "../../../lib/AlarmUtil";

const nodeTypes = {
  custom: CustomNode,
};

/**
 * Generates an array of node objects based on the provided data.
 * @param {Array} assemblies - The data to generate the nodes from.
 * @param {Function} setNodes - The function to set the generated nodes.
 */
export const generateNodes = (assemblies, setNodes, setEdges, timezone, onShowAlarmModal) => {
  const sortedAssemblies = assemblies.sort((a, b) => {
    const indexA = nodesOrder.indexOf(
      a.scaleos_type || (a.array && a.array[0].scaleos_type.split("_")[0]),
    );
    const indexB = nodesOrder.indexOf(
      b.scaleos_type || (b.array && b.array[0].scaleos_type.split("_")[0]),
    );
    return indexA - indexB;
  });

  let positionX = 20;
  let positionY = 100;
  let loadChildsPosY = sortedAssemblies.length <= 3 ? 100 : sortedAssemblies.length === 4 ? 50 : 10;
  let sourcePos =
    sortedAssemblies.length <= 3
      ? [Position.Right]
      : sortedAssemblies.length === 4
        ? [Position.Top, Position.Bottom]
        : [Position.Top, Position.Right, Position.Bottom];
  let sourceIndex = 0;
  let idItem = 1;
  let dataNodes = [];
  let powerDER = 0;
  let powerUtility = 0;

  /*
   * Nodes position (x, y) and parent/child
   */
  for (const item of sortedAssemblies) {
    let nestedId = 0;
    let loadChildsPosX = positionX;

    if (Array.isArray(item.array)) {
      let isSourcePos = false;
      const isUtilityGroup =
        item?.name === "Utility" ||
        item?.scaleos_type === "Utility" ||
        (item.array && (item.array[0]?.scaleos_type || "").toLowerCase() === "utility");
      let utilityStackPosY = loadChildsPosY;
      for (const nestedItem of item.array) {
        const obj = {
          id: `${idItem.toString()}-${nestedId.toString()}`,
          type: "custom",
          data: {
            sourcePos: !isSourcePos ? sourcePos[sourceIndex] : "left",
            nodeType: "square",
            source: nestedItem.scaleos_type,
            name: nestedItem.scaleos_name.replace("_", " "),
            power: nestedItem.power,
            percentage: nestedItem.percentage,
            alarms: nestedItem.alarms,
            alarmsCount: nestedItem.alarms_count,
            timezone: timezone,
            onShowAlarmModal: onShowAlarmModal,
            // minimize data transformations so standard types defined by API can be used throughout the app
            rawAssetData: nestedItem,
            ...(nestedItem["soc"] && { soc: nestedItem.soc }),
          },
          position: {
            x: isUtilityGroup ? positionX : loadChildsPosX,
            y: isUtilityGroup ? utilityStackPosY : loadChildsPosY,
          },
          child: loadChilds.includes(nestedItem.scaleos_type),
        };
        isSourcePos = true;
        if (isUtilityGroup) {
          utilityStackPosY += 90;
        } else {
          loadChildsPosX += 80;
        }
        nestedId += 1;
        dataNodes.push(obj);
        if (nestedItem.scaleos_type.toLowerCase() === "utility") {
          powerUtility += parseFloat(nestedItem.power);
        } else {
          powerDER += parseFloat(nestedItem.power);
        }
      }
      sourceIndex += 1;
      loadChildsPosY += 90;
      if (isUtilityGroup) {
        positionX += 200;
        loadChildsPosX = positionX;
      }
      idItem += 1;
    } else {
      if (loadChilds.includes(item.scaleos_type)) {
        const obj = {
          id: `${idItem.toString()}-${nestedId.toString()}`,
          type: "custom",
          data: {
            sourcePos: sourcePos[sourceIndex],
            nodeType: "square",
            source: item.scaleos_type,
            name: item.scaleos_name.replace("_", " "),
            power: item.power,
            percentage: item.percentage,
            alarms: item.alarms,
            alarmsCount: item.alarms_count,
            timezone: timezone,
            onShowAlarmModal: onShowAlarmModal,
            // minimize data transformations so standard types defined by API can be used throughout the app
            rawAssetData: item,
            ...(item["soc"] && { soc: item.soc }),
          },
          position: { x: loadChildsPosX, y: loadChildsPosY },
          child: true,
        };
        dataNodes.push(obj);
        loadChildsPosX += 170;
        loadChildsPosY += 90;
        sourceIndex += 1;
        powerDER += parseFloat(item.power);
      } else {
        powerUtility += item.scaleos_type.toLowerCase() === "utility" ? parseFloat(item.power) : 0;
        const obj = {
          id: `${idItem.toString()}-${nestedId.toString()}`,
          type: "custom",
          data: {
            sourcePos: "left",
            nodeType: "square",
            source: item.scaleos_type,
            name: item.scaleos_name.replace("_", " "),
            power: item.power,
            percentage: item.percentage,
            alarms: item.alarms,
            alarmsCount: item.alarms_count,
            timezone: timezone,
            onShowAlarmModal: onShowAlarmModal,
            ...(item["soc"] && { soc: item.soc }),
          },
          position: { x: positionX, y: positionY },
          child: false,
        };
        positionX += 200;
        loadChildsPosX = positionX;
        dataNodes.push(obj);
      }
      idItem += 1;
    }
  }

  // Re-center DER child rows vertically around the Load node
  const loadNode = dataNodes.find(
    (n) => n.data?.source?.toLowerCase && n.data.source.toLowerCase() === "load",
  );
  if (loadNode) {
    // preserve original row order by scanning dataNodes in order
    const seenRows = new Set();
    const rowYs = [];
    for (const n of dataNodes) {
      if (n.child) {
        const y = n.position?.y || 0;
        const key = y.toString();
        if (!seenRows.has(key)) {
          seenRows.add(key);
          rowYs.push(y);
        }
      }
    }
    const numRows = rowYs.length;
    if (numRows > 0) {
      const spacing = 90;
      const startY = loadNode.position.y - Math.round((numRows - 1) / 2) * spacing;
      const remap = new Map(rowYs.map((y, i) => [y, startY + i * spacing]));
      for (const n of dataNodes) {
        if (n.child) {
          const oldY = n.position?.y || 0;
          const newY = remap.get(oldY);
          if (typeof newY === "number") {
            n.position = { ...n.position, y: newY };
          }
        }
      }
    }
  }

  const powerSum = powerUtility + powerDER;
  const porcUtility = Math.min(
    Math.max(parseFloat(((powerUtility / powerSum) * 100).toFixed(0)), 0),
    100,
  );
  const porcDER = Math.min(Math.max(parseFloat(((powerDER / powerSum) * 100).toFixed(0)), 0), 100);

  let newDataNodes = dataNodes
    .map((item, index) => {
      if (item.data.source.toLowerCase() === "utility") {
        // do not add individual utility pies here; we'll add a single shared pie later
        return item;
      } else if (item.data.source.toLowerCase() === "load") {
        const load = {
          id: `${item.id}-pie`,
          type: "custom",
          data: {
            sourcePos: "left",
            percentage: porcDER,
            nodeType: "piechart",
            source: "load-pie",
          },
          position: {
            x: item.position.x + 120,
            y: item.position.y + 17,
          },
          child: false,
        };
        return [item, load];
      } else {
        return item;
      }
    })
    .flat();

  // Insert a single shared utility pie centered vertically among utility nodes
  const utilitySquares = newDataNodes.filter(
    (n) => n.data?.source?.toLowerCase && n.data.source.toLowerCase() === "utility",
  );
  if (utilitySquares.length > 0) {
    const minY = Math.min(...utilitySquares.map((n) => n.position.y));
    const maxY = Math.max(...utilitySquares.map((n) => n.position.y));
    const centerY = Math.round(minY + (maxY - minY) / 2);
    // place utility pie 120px to the right of the leftmost utility square
    const minX = Math.min(...utilitySquares.map((n) => n.position.x));
    const utilityPie = {
      id: `${utilitySquares[0].id}-pie-shared`,
      type: "custom",
      data: {
        sourcePos: "left",
        percentage: porcUtility,
        nodeType: "piechart",
        source: "utility-pie",
      },
      position: {
        x: minX + 120,
        y: centerY + 17,
      },
      child: false,
    };
    // ensure it's after the last utility square for edge ordering simplicity
    newDataNodes.push(utilityPie);
  }

  const utilityPieNode = newDataNodes.find(
    (n) => n.data?.source?.toLowerCase && n.data.source.toLowerCase() === "utility-pie",
  );
  const loadPieNode = newDataNodes.find(
    (n) => n.data?.source?.toLowerCase && n.data.source.toLowerCase() === "load-pie",
  );
  const loadSquareNode = newDataNodes.find(
    (n) => n.data?.source?.toLowerCase && n.data.source.toLowerCase() === "load",
  );

  const dataEdges = [];

  // Utility edges: each utility square -> shared utility pie, then shared pie -> load pie
  const utilitySquaresForEdges = newDataNodes.filter(
    (n) => n.data?.source?.toLowerCase && n.data.source.toLowerCase() === "utility",
  );
  if (utilityPieNode) {
    for (const u of utilitySquaresForEdges) {
      dataEdges.push({
        id: `e${u.id}_${utilityPieNode.id}`,
        source: u.id,
        target: utilityPieNode.id,
        type: "smoothstep",
        style: { stroke: "#868686", strokeDasharray: "2 2" },
      });
    }
    if (loadPieNode) {
      dataEdges.push({
        id: `e${utilityPieNode.id}_${loadPieNode.id}`,
        source: utilityPieNode.id,
        target: loadPieNode.id,
        type: "smoothstep",
        style: { stroke: "#868686", strokeDasharray: "2 2" },
      });
    }
  }

  // Connect load square -> load pie
  if (loadSquareNode && loadPieNode) {
    dataEdges.push({
      id: `e${loadSquareNode.id}_${loadPieNode.id}`,
      source: loadSquareNode.id,
      target: loadPieNode.id,
      type: "smoothstep",
      style: { stroke: "#868686", strokeDasharray: "2 2" },
    });
  }

  // DER rows: connect like-type nodes within the same row (same y), and row start -> load pie
  const normalizeType = (s) => s.replace(/ \d+$/, "").toLowerCase();
  const derChildren = newDataNodes.filter((n) => n.child === true);
  const rows = new Map();
  for (const n of derChildren) {
    const key = `${normalizeType(n.data.source)}_${n.position.y}`;
    if (!rows.has(key)) rows.set(key, []);
    rows.get(key).push(n);
  }
  for (const [key, nodesOfRow] of rows.entries()) {
    nodesOfRow.sort((a, b) => a.position.x - b.position.x);
    for (let i = 0; i < nodesOfRow.length - 1; i++) {
      const a = nodesOfRow[i];
      const b = nodesOfRow[i + 1];
      dataEdges.push({
        id: `e${a.id}_${b.id}`,
        source: a.id,
        target: b.id,
        type: "smoothstep",
        style: { stroke: "#868686", strokeDasharray: "2 2" },
      });
    }
    if (loadPieNode && nodesOfRow.length > 0) {
      const first = nodesOfRow[0];
      dataEdges.push({
        id: `e${loadPieNode.id}_${first.id}`,
        source: loadPieNode.id,
        target: first.id,
        type: "smoothstep",
        style: { stroke: "#868686", strokeDasharray: "2 2" },
      });
    }
  }

  setEdges(dataEdges);
  setNodes(newDataNodes);
};

const SiteDiagram = ({ site }) => {
  const [nodes, setNodes] = useNodesState([]);
  const [edges, setEdges] = useEdgesState([]);
  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false);
  const [modalAssetData, setModalAssetData] = useState(null);
  const [isAlarmsModalOpen, setIsAlarmsModalOpen] = useState(false);
  const [modalAlarms, setModalAlarms] = useState([]);
  const [modalSource, setModalSource] = useState("");

  const { isLoading, error, data } = useQuery({ ...getSitePowerCapacityQuery(site.uuid) });

  const isSupportedAsset = (asset) => {
    return ["BESS", "Solar", "Genset"].includes(asset?.scaleos_type);
  };

  const setNextAsset = (forwardDirection = true) => {
    const assetList = [];
    for (const asset of nodes) {
      const assetData = asset.data.rawAssetData;
      if (!assetData) {
        continue;
      }
      if (!isSupportedAsset(assetData)) {
        continue;
      }
      assetList.push(assetData);
    }

    const currentAssetIndex = assetList.findIndex((asset) => asset.uuid === modalAssetData.uuid);
    const forwardAssetIndex = currentAssetIndex + 1 >= assetList.length ? 0 : currentAssetIndex + 1;
    const backwardAssetIndex =
      currentAssetIndex - 1 < 0 ? assetList.length - 1 : currentAssetIndex - 1;
    const nextAsset = forwardDirection
      ? assetList[forwardAssetIndex]
      : assetList[backwardAssetIndex];

    setModalAssetData(nextAsset);
    setIsAssetModalOpen(true);
  };

  const handleOpenAssetModal = (event, node) => {
    const assetData = node.data.rawAssetData;

    if (!isSupportedAsset(assetData)) {
      return;
    }
    setModalAssetData(assetData);
    setIsAssetModalOpen(true);
  };

  const handleShowAlarmModal = (alarms, source) => {
    const allAlarms = sortAlarmsBySeverity(alarms).map((alarm) => ({
      ...alarm,
      description: alarm.name,
      timestamp: alarm.date,
    }));
    setModalAlarms(allAlarms);
    setModalSource(source);
    setIsAlarmsModalOpen(true);
  };

  useEffect(() => {
    if (data && data.assemblies.length > 0) {
      generateNodes(data.assemblies, setNodes, setEdges, site?.timezone, handleShowAlarmModal);
    } else {
      setNodes([]);
      setEdges([]);
    }
  }, [data]);

  if (isLoading)
    return (
      <div className="site-diagram">
        <Box display="flex" flexDirection="row" mt="20px" ml="20px" alignItems="baseline">
          <Box>
            <Typography variant="h4" color="secondary">
              Assets
            </Typography>
          </Box>
        </Box>
        <Loading />
      </div>
    );
  if (error)
    return (
      <div className="site-diagram">
        <Box display="flex" flexDirection="row" mt="20px" ml="20px" alignItems="baseline">
          <Box>
            <Typography variant="h4" color="secondary">
              Assets
            </Typography>
          </Box>
        </Box>
        <DataComponentError />
      </div>
    );
  if (data) {
    return (
      <Box className="site-diagram" sx={{ position: "relative", height: "390px" }}>
        <AssetDetailModal
          isOpen={isAssetModalOpen}
          onClose={() => setIsAssetModalOpen(false)}
          assetData={modalAssetData}
          siteData={site}
          setNextAsset={setNextAsset}
        />

        <AlarmsModal
          timezone={site?.timezone}
          open={isAlarmsModalOpen}
          title="Active Alarms"
          alarms={modalAlarms}
          onClose={() => setIsAlarmsModalOpen(false)}
          subtitle={modalSource}
          hasAssets={true}
        />

        <Box display="flex" flexDirection="row" mt="20px" ml="20px" alignItems="baseline">
          <Box>
            <Typography variant="body2" color="secondary">
              Assets
            </Typography>
          </Box>
          <Box pl="2em">
            <Typography variant="subtitle1" color="grey">
              <TimeAgo date={data.last_update[0] + " UTC"} />
            </Typography>
          </Box>
        </Box>
        <ReactFlowProvider>
          <FlowChild
            nodes={nodes}
            edges={edges}
            handleOpenAssetModal={handleOpenAssetModal}
            onShowAlarmModal={handleShowAlarmModal}
          />
        </ReactFlowProvider>
        <OtherAlarms
          alarms={data.other_alarms}
          timezone={site?.timezone}
          onShowAlarmModal={handleShowAlarmModal}
        />
      </Box>
    );
  }
};

// this component formulation is strange, refactor it.
const FlowChild = (props) => {
  const snapGrid = [10, 10];
  const [flowInstance, setFlowInstance] = useState(null);
  const nodesInitialized = useNodesInitialized();

  useEffect(() => {
    if (flowInstance && nodesInitialized) {
      flowInstance.fitView({ minZoom: 1, maxZoom: 1 });
    }
  }, [flowInstance, nodesInitialized]);

  if (props?.nodes.length > 0) {
    return (
      <ReactFlow
        className="grey-background"
        style={{ top: "-40px" }}
        nodes={props.nodes}
        edges={props.edges}
        snapToGrid={true}
        snapGrid={snapGrid}
        nodeTypes={nodeTypes}
        elementsSelectable={false}
        nodesConnectable={false}
        nodesDraggable={false}
        zoomOnScroll={false}
        panOnScroll={false}
        panOnScrollMode={false}
        zoomOnDoubleClick={false}
        panOnDrag={false}
        zoomOnPinch={false}
        preventScrolling={false}
        onInit={setFlowInstance}
        onNodeMouseEnter={() => {}}
        onNodeClick={props.handleOpenAssetModal}
      />
    );
  }
};

FlowChild.propTypes = {
  nodes: PropTypes.array.isRequired,
  edges: PropTypes.array.isRequired,
  handleOpenAssetModal: PropTypes.func.isRequired,
  onShowAlarmModal: PropTypes.func.isRequired,
};

SiteDiagram.propTypes = {
  site: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  error: PropTypes.object,
};

export default SiteDiagram;
